import {ChangeDetectionStrategy, Component, computed, Inject, signal} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {GenerellEpostTilMedlemmerSpesifikasjon, GruppeMedlemListeModell, GruppeModell, SendEpostResultatModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatIconModule} from "@angular/material/icon";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {TranslateModule} from "@ngx-translate/core";
import {FeltValidatorer, htmlTekst} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {VelgMedlemmerKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer/velg-medlemmer";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";

const debug = initDebugger('sendEpostDialogKomponent');
type Status = 'sender' | 'sendt' | 'feilet' | 'ikkesendt';

@Component({
    selector: 'send-epost-dialog',
    templateUrl: 'send-epost-dialog.html',
    styleUrls: ['send-epost-dialog.scss'],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        TranslateModule,
        MatFormField,
        MatLabel,
        VelgMedlemmerKomponent,
        RiktekstRedigeringKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendEpostDialogKomponent {

    static vis(dialog: MatDialog, overskrift: string, valgbareMedlemmer: GruppeMedlemListeModell[], valgteMedlemmer: GruppeMedlemListeModell[], sendEpost: (epostTilMedlemmerSpesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon) => Promise<SendEpostResultatModell>): MatDialogRef<SendEpostDialogKomponent, void> {
        return dialog.open(SendEpostDialogKomponent, {data: {overskrift, valgbareMedlemmer, valgteMedlemmer, sendEpost} as SendEpostDialogParametre});
    }

    skjema = this.formBuilder.group({
        beskjed: ['', [Validators.required, ...FeltValidatorer.htmlTekst]],
        mottakere: this.formBuilder.control(<GruppeMedlemListeModell[]>[], Validators.required)
    })
    status = signal<Status>('ikkesendt');
    antallSendt = signal(0);
    antallIkkeSendt = signal(0);
    totaltSomSkalSendes = computed(() => this.antallSendt() + this.antallIkkeSendt());
    medlemmerSomDetIkkeBleSendtTil = signal<GruppeMedlemListeModell[]>([]);

    overskrift: string;

    private sendEpost: (epostTilMedlemmerSpesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon) => Promise<SendEpostResultatModell>;

    valgbareMedlemmer: GruppeMedlemListeModell[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) parametre: SendEpostDialogParametre,
                private formBuilder: FormBuilder,
                public dialogRef: MatDialogRef<SendEpostDialogKomponent>) {
        this.valgbareMedlemmer = parametre.valgbareMedlemmer;
        this.skjema.controls.mottakere.patchValue(parametre.valgteMedlemmer)
        this.overskrift = parametre.overskrift;
        this.sendEpost = parametre.sendEpost;
    }

    send() {
        debug("Sender epost")
        this.status.set('sender');
        this.sendEpost(this.modellSomSpesifikasjon()).then(result => {
            this.status.set('sendt');
            this.antallSendt.set(result.sendt.length);
            this.antallIkkeSendt.set(result.ikkeSendt.length);
            this.medlemmerSomDetIkkeBleSendtTil.set(result.ikkeSendt.map(medlemId => this.valgbareMedlemmer.find(medlem => medlem.id === medlemId)!))
        }).catch(e => {
            this.status.set('feilet');
            throw e;
        })
    }

    //private hjelpemetoder

    private modellSomSpesifikasjon(): GenerellEpostTilMedlemmerSpesifikasjon {
        return {
            beskjed: this.skjema.controls.beskjed.value!,
            mottakere: this.skjema.controls.mottakere.value!.map(medlem=>medlem.id)
        };
    }
}

export interface SendEpostDialogParametre {
    overskrift: string;
    valgbareMedlemmer: GruppeMedlemListeModell[];
    valgteMedlemmer: GruppeMedlemListeModell[];
    sendEpost: (epostTilMedlemmerSpesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon) => Promise<SendEpostResultatModell>;
}
